import type {
  CreatorFinalLinkValidationChannelError,
  CreatorOrderChannelError,
  CreatorOrderV2Envelope,
  CreatorOrderV2ErrorPayload,
  CreatorOrderV2Result
} from '@/interface/creatorOrderV2';

export const CREATOR_ORDER_V2_ERROR_CODE = {
  ORDER_CHANNELS_REQUIRED: 47001,
  CHANNEL_SET_MISMATCH: 47002,
  CHANNEL_ADD_NOT_ALLOWED: 47003,
  CHANNEL_CANNOT_BE_RE_ADDED: 47004,
  CHANNEL_DATA_UNAVAILABLE: 47005,
  ORDER_STATE_CONFLICT: 47006,
  CHANNEL_SUBMISSION_CONFLICT: 47007,
  NOT_PRIMARY_OPERATOR: 47009,
  ORDER_API_VERSION_UNSUPPORTED: 47010,
  PRODUCT_ORDER_NOT_FOUND: 47011,
  PRODUCT_ORDER_ACCESS_DENIED: 47012,
  IDEMPOTENCY_KEY_CONFLICT: 47013,
  PACKAGE_PRICE_PREDICTION_UNAVAILABLE: 47014,
  PACKAGE_PRICE_CONTENT_TYPE_UNSUPPORTED: 47015,
  QUOTE_AMOUNT_OUT_OF_RANGE: 47016,
  CHANNEL_BOUND_BY_OTHER: 47017
} as const;

export type CreatorOrderV2RecoveryAction =
  | 'REFRESH_AND_RETRY'
  | 'MARK_CHANNEL_ERRORS'
  | 'REFRESH_QUOTE'
  | 'KEEP_FORM_AND_RETRY'
  | 'REFRESH_WITHOUT_REPLAY'
  | 'REFRESH_DELIVERY_REVISION'
  | 'DISABLE_WRITES'
  | 'REQUIRE_V2'
  | 'EXIT_DETAIL'
  | 'DENY_ACCESS'
  | 'STOP_AUTOMATIC_RETRY'
  | 'HIDE_RECOMMENDATION'
  | 'RESELECT_CONTENT_TYPE'
  | 'UNKNOWN';

const RECOVERY_ACTIONS: Record<number, CreatorOrderV2RecoveryAction> = {
  40002: 'REFRESH_WITHOUT_REPLAY',
  40009: 'REFRESH_WITHOUT_REPLAY',
  40011: 'DENY_ACCESS',
  40004: 'EXIT_DETAIL',
  [CREATOR_ORDER_V2_ERROR_CODE.ORDER_CHANNELS_REQUIRED]: 'REFRESH_AND_RETRY',
  [CREATOR_ORDER_V2_ERROR_CODE.CHANNEL_SET_MISMATCH]: 'MARK_CHANNEL_ERRORS',
  [CREATOR_ORDER_V2_ERROR_CODE.CHANNEL_ADD_NOT_ALLOWED]: 'REFRESH_QUOTE',
  [CREATOR_ORDER_V2_ERROR_CODE.CHANNEL_CANNOT_BE_RE_ADDED]:
    'MARK_CHANNEL_ERRORS',
  [CREATOR_ORDER_V2_ERROR_CODE.CHANNEL_DATA_UNAVAILABLE]: 'KEEP_FORM_AND_RETRY',
  [CREATOR_ORDER_V2_ERROR_CODE.ORDER_STATE_CONFLICT]: 'REFRESH_WITHOUT_REPLAY',
  [CREATOR_ORDER_V2_ERROR_CODE.CHANNEL_SUBMISSION_CONFLICT]:
    'REFRESH_DELIVERY_REVISION',
  [CREATOR_ORDER_V2_ERROR_CODE.NOT_PRIMARY_OPERATOR]: 'DISABLE_WRITES',
  [CREATOR_ORDER_V2_ERROR_CODE.ORDER_API_VERSION_UNSUPPORTED]: 'REQUIRE_V2',
  [CREATOR_ORDER_V2_ERROR_CODE.PRODUCT_ORDER_NOT_FOUND]: 'EXIT_DETAIL',
  [CREATOR_ORDER_V2_ERROR_CODE.PRODUCT_ORDER_ACCESS_DENIED]: 'DENY_ACCESS',
  [CREATOR_ORDER_V2_ERROR_CODE.IDEMPOTENCY_KEY_CONFLICT]:
    'STOP_AUTOMATIC_RETRY',
  [CREATOR_ORDER_V2_ERROR_CODE.PACKAGE_PRICE_PREDICTION_UNAVAILABLE]:
    'HIDE_RECOMMENDATION',
  [CREATOR_ORDER_V2_ERROR_CODE.PACKAGE_PRICE_CONTENT_TYPE_UNSUPPORTED]:
    'RESELECT_CONTENT_TYPE',
  [CREATOR_ORDER_V2_ERROR_CODE.QUOTE_AMOUNT_OUT_OF_RANGE]:
    'KEEP_FORM_AND_RETRY',
  [CREATOR_ORDER_V2_ERROR_CODE.CHANNEL_BOUND_BY_OTHER]: 'MARK_CHANNEL_ERRORS'
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

const normalizeFinalLinkValidationChannels = (
  value: unknown
): CreatorFinalLinkValidationChannelError[] =>
  Array.isArray(value)
    ? value.flatMap((item) => {
        if (!isRecord(item)) return [];
        const channelKey =
          typeof item.channelKey === 'string'
            ? item.channelKey.trim() || undefined
            : undefined;
        const orderId =
          typeof item.orderId === 'string'
            ? item.orderId.trim() || undefined
            : undefined;
        if (!channelKey && !orderId) return [];
        return [
          {
            channelKey,
            orderId,
            code:
              typeof item.code === 'number' && Number.isFinite(item.code)
                ? item.code
                : undefined,
            message:
              typeof item.message === 'string' ? item.message : undefined,
            isExist:
              typeof item.isExist === 'boolean' ? item.isExist : undefined,
            isMatchedInfluencer:
              typeof item.isMatchedInfluencer === 'boolean'
                ? item.isMatchedInfluencer
                : undefined,
            isContentValid:
              typeof item.isContentValid === 'boolean'
                ? item.isContentValid
                : undefined,
            contentInvalidReasons: Array.isArray(item.contentInvalidReasons)
              ? item.contentInvalidReasons.filter(
                  (reason): reason is string => typeof reason === 'string'
                )
              : undefined
          }
        ];
      })
    : [];

const getChannelErrors = (
  payload?: CreatorOrderV2ErrorPayload
): CreatorOrderChannelError[] => {
  if (Array.isArray(payload?.channelErrors)) return payload.channelErrors;

  // Final Link AI review returns per-channel failures under `channels`.
  // Normalize its numeric codes to the existing UI error contract.
  if (!Array.isArray(payload?.channels)) return [];
  return payload.channels.flatMap((channel: unknown) => {
    if (
      !isRecord(channel) ||
      typeof channel.channelKey !== 'string' ||
      !channel.channelKey.trim() ||
      !(
        (typeof channel.code === 'string' && channel.code.trim()) ||
        (typeof channel.code === 'number' && Number.isFinite(channel.code))
      )
    ) {
      return [];
    }
    return [
      {
        channelKey: channel.channelKey,
        code: String(channel.code),
        ...(typeof channel.field === 'string' ? { field: channel.field } : {}),
        ...(typeof channel.message === 'string'
          ? { message: channel.message }
          : {})
      }
    ];
  });
};

export class CreatorOrderV2ApiError extends Error {
  readonly code: number;
  readonly reason?: string;
  readonly cause?: string;
  readonly channelErrors: CreatorOrderChannelError[];
  readonly payload?: CreatorOrderV2ErrorPayload;
  readonly recoveryAction: CreatorOrderV2RecoveryAction;

  constructor({
    code,
    message,
    payload
  }: {
    code: number;
    message: string;
    payload?: CreatorOrderV2ErrorPayload;
  }) {
    super(message);
    this.name = 'CreatorOrderV2ApiError';
    this.code = code;
    this.reason = payload?.reason;
    this.cause = typeof payload?.cause === 'string' ? payload.cause : undefined;
    this.channelErrors = getChannelErrors(payload);
    this.payload = payload;
    this.recoveryAction = RECOVERY_ACTIONS[code] ?? 'UNKNOWN';
  }
}

export class CreatorOrderV2ContractError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'CreatorOrderV2ContractError';
  }
}

export const isCreatorOrderV2ApiError = (
  error: unknown
): error is CreatorOrderV2ApiError => error instanceof CreatorOrderV2ApiError;

export const getCreatorFinalLinkValidationChannels = (
  error: unknown
): CreatorFinalLinkValidationChannelError[] => {
  if (error instanceof CreatorOrderV2ApiError) {
    return normalizeFinalLinkValidationChannels(error.payload?.channels);
  }
  if (!isRecord(error)) return [];

  const payload = isRecord(error.payload)
    ? error.payload
    : isRecord(error.responseData) && isRecord(error.responseData.payload)
      ? error.responseData.payload
      : undefined;
  return normalizeFinalLinkValidationChannels(payload?.channels);
};

export const assertCreatorOrderV2Success = <T>(
  response: unknown
): CreatorOrderV2Result<T> => {
  if (!isRecord(response) || typeof response.code !== 'number') {
    throw new CreatorOrderV2ContractError(
      'Invalid V2 order response: numeric code is required.'
    );
  }

  const envelope = response as unknown as CreatorOrderV2Envelope<T>;
  if (envelope.code !== 0) {
    throw new CreatorOrderV2ApiError({
      code: envelope.code,
      message: envelope.message || 'V2 order request failed.',
      payload: envelope.payload
    });
  }

  return {
    data: envelope.data,
    paginate: envelope.paginate
  };
};
